import { UzButton, UzDialog, UzInput } from '@utazzo/ui';
import React, { useEffect, useState } from 'react';

import { useChangeMyPassword } from '../use-my-profile';

interface ChangePasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Self-service password change (ADR-0040). Current password is
 * verified server-side before the new hash lands. New password must
 * be at least 8 characters AND differ from the current one.
 */
export const ChangePasswordDialog = ({
  open,
  onOpenChange,
}: ChangePasswordDialogProps): React.JSX.Element => {
  const change = useChangeMyPassword();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Clear the form every time the dialog opens so a previous typed
  // password doesn't sit in memory after a cancel.
  useEffect(() => {
    if (open) {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    }
  }, [open]);

  const tooShort = newPassword.length > 0 && newPassword.length < 8;
  const mismatch = confirmPassword.length > 0 && confirmPassword !== newPassword;
  const canSubmit =
    currentPassword.length > 0 &&
    newPassword.length >= 8 &&
    confirmPassword === newPassword &&
    currentPassword !== newPassword;

  const submit = async (): Promise<void> => {
    if (!canSubmit) return;
    await change.mutateAsync({ currentPassword, newPassword });
    onOpenChange(false);
  };

  return (
    <UzDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Change password"
      description="Pick a new password at least 8 characters long. We'll verify your current one first."
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={() => onOpenChange(false)} disabled={change.isPending}>
            Cancel
          </UzButton>
          <UzButton onClick={submit} loading={change.isPending} disabled={!canSubmit}>
            Change password
          </UzButton>
        </div>
      }
    >
      <div className="space-y-3">
        <UzInput
          name="currentPassword"
          label="Current password"
          type="password"
          autoComplete="current-password"
          required
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          disabled={change.isPending}
        />
        <UzInput
          name="newPassword"
          label="New password"
          type="password"
          autoComplete="new-password"
          required
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          disabled={change.isPending}
          error={tooShort ? 'Must be at least 8 characters.' : undefined}
        />
        <UzInput
          name="confirmPassword"
          label="Confirm new password"
          type="password"
          autoComplete="new-password"
          required
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          disabled={change.isPending}
          error={mismatch ? "Doesn't match the new password." : undefined}
        />
      </div>
    </UzDialog>
  );
};
